import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, LabelList } from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
	Button,
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	HorizontalScroll,
	Skeleton,
	useIsMobile,
	formatCurrency,
} from "@hotelise/ui";

import {
	getDashboardSelectedYear,
	getDashboardYearReport,
	getDashboardYearReportData,
	setDashboardReportYear,
} from "state/modules/dashboard";
import { PropertyYearReport } from "../../interfaces/reports";

const labels = [
	"Jan", "Feb", "Mar", "Apr", "May", "Jun",
	"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const DATASETS_INFO = [
	{
		id: "income",
		label: "Income",
		unit: "$",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) => item.calculations.totalInCurrency.totalIncomeInCurrency
			),
	},
	{
		id: "payouts",
		label: "Payouts",
		unit: "$",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) =>
					item.calculations.totalInCurrency.netOwnerIncomeInCurrency
			),
	},
	{
		id: "comission",
		label: "Commission",
		unit: "$",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) => item.calculations.totalInCurrency.totalHoteliseFeeInCurrency
			),
	},
	{
		id: "expenses",
		label: "Expenses",
		unit: "$",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) => item.calculations.totalInCurrency.totalExpensesInCurrency
			),
	},
	{
		id: "cleaning",
		label: "Cleaning",
		unit: "$",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) => item.calculations.totalInCurrency.totalCleaningInCurrency
			),
	},
	{
		id: "adr",
		label: "ADR",
		unit: "$",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) => item.calculations.totalInCurrency.adr
			),
	},
	{
		id: "revpar",
		label: "RevPAR",
		unit: "$",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) => item.calculations.totalInCurrency.revPar
			),
	},
	{
		id: "occupancy",
		label: "Occupancy",
		unit: "%",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map((item) => item.calculations.occupancyRate),
	},
	{
		id: "alos",
		label: "ALOS",
		unit: "days",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map((item) => item.calculations.alos),
	},
	{
		id: "ownerBlockedDays",
		label: "OOO",
		unit: "days",
		getData: (data: PropertyYearReport | null) =>
			(data?.yearData || []).map(
				(item) => item.calculations.reservationPeriod?.ownerBlockedDays ?? 0
			),
	},
];

const chartConfig = Object.fromEntries(
	DATASETS_INFO.map((d) => [d.id, { label: d.label, color: "hsl(var(--primary))" }])
);

interface BarLabelProps {
	x?: number;
	y?: number;
	width?: number;
	value?: number | string;
	isMobile: boolean;
	formatShort: (v: number) => string;
	formatFull: (v: number) => string;
}

const BarLabel = ({
	x,
	y,
	width,
	value,
	isMobile,
	formatShort,
	formatFull,
}: BarLabelProps) => {
	const labelX = (x ?? 0) + (width ?? 0) / 2;
	const labelY = (y ?? 0) - (isMobile ? 4 : 6);
	const num = typeof value === "number" ? value : Number(value);
	const safeNum = Number.isNaN(num) ? 0 : num;
	const text = isMobile ? formatShort(safeNum) : formatFull(safeNum);

	return (
		<text
			x={labelX}
			y={labelY}
			textAnchor="middle"
			fill="hsl(var(--muted-foreground))"
			fontSize={isMobile ? 9 : 11}
		>
			{text}
		</text>
	);
};

const SKEL_BARS = [
	"Jan", "Feb", "Mar", "Apr", "May", "Jun",
	"Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

interface Props {
	isLoading: boolean;
}

const DashboardIncomeChart = ({ isLoading }: Props) => {
	const dispatch = useDispatch();
	const isMobile = useIsMobile();

	const year = useSelector(getDashboardSelectedYear);
	const report: PropertyYearReport | null = useSelector(
		getDashboardYearReportData
	);

	const [selectedDataset, setSelectedDataset] = useState(DATASETS_INFO[0].id);

	const handleChangeYear = (newYear: number) => {
		dispatch(setDashboardReportYear(newYear));
		dispatch(getDashboardYearReport());
	};

	const selectedInfo =
		DATASETS_INFO.find((d) => d.id === selectedDataset) || DATASETS_INFO[0];

	const chartData = useMemo(() => {
		const values = selectedInfo.getData(report);
		return labels.map((month, i) => ({
			month,
			[selectedDataset]: values[i] ?? 0,
		}));
	}, [selectedDataset, report]);

	const formatValue = (value: number) => {
		if (selectedInfo.unit === "%") return `${Math.round(value)}%`;
		if (selectedInfo.unit === "days") return `${value.toFixed(1)}d`;
		return formatCurrency(Math.round(value));
	};

	const formatValueShort = (value: number) => {
		if (selectedInfo.unit === "%") return `${Math.round(value)}%`;
		if (selectedInfo.unit === "days") return `${value.toFixed(1)}d`;
		return formatCurrency(Math.round(value), true);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const renderBarLabel = useCallback(
		(props: any) => (
			<BarLabel
				{...props}
				isMobile={isMobile}
				formatShort={formatValueShort}
				formatFull={formatValue}
			/>
		),
		[isMobile, selectedInfo]
	);

	if (isLoading) {
		return (
			<div className="bg-card rounded-lg p-6 border">
				<div className="flex items-center gap-4 mb-6">
					<div className="flex items-center gap-2">
						<Skeleton className="h-9 w-9 rounded-md" />
						<Skeleton className="h-6 w-16" />
						<Skeleton className="h-9 w-9 rounded-md" />
					</div>
					<Skeleton className="h-6 w-40" />
				</div>
				<div className="flex flex-wrap gap-2 mb-6">
					{DATASETS_INFO.map((ds) => (
						<Skeleton key={ds.id} className="h-8 w-20 rounded-md" />
					))}
				</div>
				<div
					className={`${isMobile ? "h-[300px]" : "h-[400px]"} flex items-end gap-2 pt-8 pb-10 px-4`}
				>
					{SKEL_BARS.map((id) => (
						<div
							key={id}
							className="flex-1 flex flex-col items-center gap-2"
						>
							<Skeleton
								className="w-full rounded-t-md"
								style={{ height: `${Math.random() * 60 + 20}%` }}
							/>
							<Skeleton className="h-3 w-6" />
						</div>
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="bg-card rounded-lg p-6 border relative overflow-hidden">
			{/* Header with year nav */}
			<div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
				<div className="flex items-center gap-2">
					<Button
						variant="outline"
						size="icon"
						onClick={() => handleChangeYear(year - 1)}
						disabled={isLoading || year <= 2020}
					>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="text-lg font-semibold min-w-[80px] text-center">
						{year}
					</span>
					<Button
						variant="outline"
						size="icon"
						onClick={() => handleChangeYear(year + 1)}
						disabled={isLoading || year >= 2030}
					>
						<ChevronRight className="h-4 w-4" />
					</Button>
				</div>
				<h2 className="text-lg font-semibold">
					{selectedInfo.label} for {year}
				</h2>
			</div>

			{/* KPI Buttons */}
			{isMobile ? (
				<HorizontalScroll className="-mx-6 px-6 mb-6" fadeBg="card">
					{DATASETS_INFO.map((ds) => (
						<Button
							key={ds.id}
							variant={selectedDataset === ds.id ? "default" : "outline"}
							size="sm"
							onClick={() => setSelectedDataset(ds.id)}
							className="text-xs shrink-0"
						>
							{ds.label}
						</Button>
					))}
				</HorizontalScroll>
			) : (
				<div className="flex flex-wrap gap-2 mb-6">
					{DATASETS_INFO.map((ds) => (
						<Button
							key={ds.id}
							variant={selectedDataset === ds.id ? "default" : "outline"}
							size="sm"
							onClick={() => setSelectedDataset(ds.id)}
							className="text-xs"
						>
							{ds.label}
						</Button>
					))}
				</div>
			)}

			{/* Chart */}
			<ChartContainer
				config={chartConfig}
				className={`${isMobile ? "h-[300px]" : "h-[400px]"} w-full [&>div]:!aspect-auto`}
			>
				<BarChart
					data={chartData}
					margin={{
						top: isMobile ? 25 : 40,
						right: isMobile ? 10 : 30,
						left: isMobile ? 10 : 20,
						bottom: isMobile ? 20 : 5,
					}}
				>
					<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
					<XAxis
						dataKey="month"
						className="text-xs"
						tick={{ fontSize: isMobile ? 10 : 12 }}
						angle={isMobile ? -45 : 0}
						textAnchor={isMobile ? "end" : "middle"}
						height={isMobile ? 60 : 30}
					/>
					<YAxis
						className="text-xs"
						tick={{ fontSize: isMobile ? 9 : 12 }}
						tickFormatter={(value) => formatValueShort(value)}
						width={isMobile ? 35 : 50}
					/>
					<ChartTooltip
						content={<ChartTooltipContent />}
						formatter={(value: number) => [
							formatValue(value),
							selectedInfo.label,
						]}
					/>
					<Bar dataKey={selectedDataset} radius={[4, 4, 0, 0]}>
						<LabelList dataKey={selectedDataset} content={renderBarLabel} />
						{chartData.map((entry) => (
							<Cell key={entry.month} fill="hsl(var(--primary))" />
						))}
					</Bar>
				</BarChart>
			</ChartContainer>
		</div>
	);
};

export default DashboardIncomeChart;
